import React, { useCallback } from 'react';
import { GripVertical } from 'lucide-react';
import { cn } from '../../../lib/utils';

interface CanvasProjectionSplitHandleProps {
  readonly ratio: number;
  readonly onRatioChange: (ratio: number) => void;
}

const MIN_RATIO = 0.2;
const MAX_RATIO = 0.8;
const KEYBOARD_STEP = 0.05;

function clampRatio(value: number): number {
  if (!Number.isFinite(value)) return 0.5;
  return Math.min(MAX_RATIO, Math.max(MIN_RATIO, value));
}

export const CanvasProjectionSplitHandle: React.FC<CanvasProjectionSplitHandleProps> = ({
  ratio,
  onRatioChange,
}) => {
  const updateFromClientX = useCallback((clientX: number, container: HTMLElement) => {
    const rect = container.getBoundingClientRect();
    if (rect.width <= 0) return;
    onRatioChange(clampRatio((clientX - rect.left) / rect.width));
  }, [onRatioChange]);

  const handlePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const container = event.currentTarget.parentElement;
    if (!container) return;

    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    updateFromClientX(event.clientX, container);

    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const handlePointerMove = (moveEvent: PointerEvent) => {
      updateFromClientX(moveEvent.clientX, container);
    };

    const stopDrag = () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', stopDrag);
      window.removeEventListener('pointercancel', stopDrag);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', stopDrag, { once: true });
    window.addEventListener('pointercancel', stopDrag, { once: true });
  }, [updateFromClientX]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      onRatioChange(clampRatio(ratio - KEYBOARD_STEP));
      return;
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      onRatioChange(clampRatio(ratio + KEYBOARD_STEP));
      return;
    }
    if (event.key === 'Home') {
      event.preventDefault();
      onRatioChange(MIN_RATIO);
      return;
    }
    if (event.key === 'End') {
      event.preventDefault();
      onRatioChange(MAX_RATIO);
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onRatioChange(0.5);
    }
  }, [onRatioChange, ratio]);

  return (
    <div
      role="separator"
      aria-label="Resize canvas panes"
      aria-orientation="vertical"
      aria-valuemin={Math.round(MIN_RATIO * 100)}
      aria-valuemax={Math.round(MAX_RATIO * 100)}
      aria-valuenow={Math.round(ratio * 100)}
      tabIndex={0}
      onPointerDown={handlePointerDown}
      onKeyDown={handleKeyDown}
      className={cn(
        'group relative z-30 flex h-full w-2 shrink-0 cursor-col-resize items-center justify-center',
        'border-x border-border bg-background/80 outline-none transition-colors',
        'hover:bg-primary/10 focus-visible:bg-primary/10 focus-visible:ring-2 focus-visible:ring-ring',
      )}
    >
      <div className="flex h-12 w-4 items-center justify-center rounded-full border border-border bg-background text-muted-foreground shadow-sm transition-colors group-hover:text-foreground">
        <GripVertical className="h-4 w-4" />
      </div>
    </div>
  );
};
